/**
 * Integrações › **Notificações push** (D854, L7a-033; D688 / ADR-0139) - the Firebase Cloud Messaging slot the ride and offer pushes leave through: the
 * service-account key (WRITE-ONLY: sealed in the vault; the screen shows only a masked hint and the Firebase project id) and the switch. The badges say
 * whether a key is stored and whether push is going out now. Composes `@vello/web-theme` only; the rules are `normalizePushForm`.
 */
import { type FormEvent, type ReactElement, useEffect, useId, useState } from 'react';
import type { PushFcmSlotView } from '@vello/api-client';
import { Badge, Button, Cluster, Field, FormGrid, Notice, Panel, Select, Stack, Text } from '@vello/web-theme';
import {
  type PushFormIssue,
  type PushIntegrationForm,
  emptyPushForm,
  formatMarketInstant,
  getPushIntegration,
  normalizePushForm,
  pushFormFromView,
  setPushIntegration,
} from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { JsonArea } from './otp-slot-fields';

const ISSUE_KEY: Readonly<Record<PushFormIssue['code'], string>> = {
  KEY_NOT_JSON: 'int.push.err.notJson',
  KEY_NOT_SERVICE_ACCOUNT: 'int.push.err.notServiceAccount',
  KEY_REQUIRED: 'int.push.err.keyRequired',
};

export function PushPanel(): ReactElement {
  const { runtime } = useAdmin();
  const { t, lang } = useT();
  const { fail } = useVocab();
  const id = useId();
  const [view, setView] = useState<PushFcmSlotView | undefined>(undefined);
  const [form, setForm] = useState<PushIntegrationForm>(() => emptyPushForm());
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getPushIntegration(runtime).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setView(res.value.fcm);
        setForm(pushFormFromView(res.value.fcm));
      } else setMsg({ tone: 'danger', text: t('int.load.failed', { code: fail(res.error) }) });
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, t]);

  const configured = view?.configured === true;

  const onSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    const normalized = normalizePushForm(form, configured);
    if (!normalized.ok) {
      const issue = normalized.error[0];
      setError(issue !== undefined ? t(ISSUE_KEY[issue.code]) : undefined);
      return;
    }
    setError(undefined);
    setBusy(true);
    const res = await setPushIntegration(runtime, normalized.value);
    setBusy(false);
    if (res.ok) {
      setView(res.value);
      setForm(pushFormFromView(res.value));
      setMsg({ tone: 'success', text: t('int.saved') });
    } else setMsg({ tone: 'danger', text: t('int.save.failed', { code: fail(res.error) }) });
  };

  const sending = configured && view?.enabled === true;
  return (
    <Panel>
      <Stack gap={4}>
        <Cluster gap={3} align="baseline" wrap>
          <Text as="h2" role="title">{t('int.push.heading')}</Text>
          <Badge tone={configured ? 'success' : 'warning'}>{configured ? t('int.status.configured') : t('int.status.notConfigured')}</Badge>
          {view !== undefined && <Badge tone={sending ? 'success' : 'neutral'}>{sending ? t('int.push.state.on') : t('int.push.state.off')}</Badge>}
        </Cluster>
        <Text as="p" tone="secondary">{t('int.push.hint')}</Text>
        {view?.projectId !== undefined && <Text as="p" tone="secondary">{t('int.push.project', { project: view.projectId })}</Text>}
        {view?.updatedAt !== undefined && <Text as="p" role="caption" tone="secondary">{t('int.push.updated', { when: formatMarketInstant(view.updatedAt, undefined, lang) })}</Text>}
        <form onSubmit={(e) => void onSave(e)} noValidate aria-label={t('int.push.heading')}>
          <Stack gap={4}>
            <FormGrid>
              <Field label={t('int.field.enabled')} htmlFor={`${id}-enabled`} hint={t('int.push.field.enabled.hint')}>
                <Select id={`${id}-enabled`} value={form.enabled ? 'on' : 'off'} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.value === 'on' }))}>
                  <option value="on">{t('int.field.enabled.on')}</option>
                  <option value="off">{t('int.field.enabled.off')}</option>
                </Select>
              </Field>
            </FormGrid>
            <Field
              label={t('int.push.field.serviceAccount')}
              htmlFor={`${id}-key`}
              hint={error ?? (configured ? t('int.push.field.serviceAccount.hintConfigured', { hint: view?.maskedHint ?? '' }) : t('int.push.field.serviceAccount.hint'))}
            >
              <JsonArea id={`${id}-key`} autoComplete="off" spellCheck={false} value={form.serviceAccount} onChange={(e) => setForm((f) => ({ ...f, serviceAccount: e.target.value }))} aria-invalid={error !== undefined} />
            </Field>
            {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
            <div>
              <Button type="submit" variant="secondary" disabled={busy}>{busy ? t('int.save.busy') : t('int.save')}</Button>
            </div>
          </Stack>
        </form>
        <Text as="p" role="caption" tone="secondary">{t('int.push.delivery.note')}</Text>
      </Stack>
    </Panel>
  );
}
